/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { materialAttenuation } from './materials'
import { pathFractionToPoint, rayHeightAt, verticalBarrierAttenuationDb } from './obstacles'
import type { FloorWall, WallMaterial } from './types'

export const DEFAULT_WALL_HEIGHT_M = 2.7
export const DEFAULT_WALL_THICKNESS_M = 0.15

export type WallDims = { heightM: number; thicknessM: number }

export function resolveWallDimensions(w: FloorWall): WallDims {
  return {
    heightM: w.heightM ?? DEFAULT_WALL_HEIGHT_M,
    thicknessM: w.thicknessM ?? DEFAULT_WALL_THICKNESS_M,
  }
}

export function formatWallSize(w: FloorWall): string {
  const { heightM, thicknessM } = resolveWallDimensions(w)
  return `${thicknessM.toFixed(2)} m thick · ${heightM.toFixed(1)} m tall`
}

export type WallDrawStyle = {
  stroke: string
  glow: string
  widthMul: number
}

const WALL_DRAW_STYLES: Record<WallMaterial, WallDrawStyle> = {
  drywall: { stroke: '#f1f5f9', glow: 'rgba(241,245,249,0.35)', widthMul: 1 },
  brick: { stroke: '#fb923c', glow: 'rgba(251,146,60,0.38)', widthMul: 1.15 },
  concrete: { stroke: '#c4b5fd', glow: 'rgba(196,181,253,0.35)', widthMul: 1.15 },
  reinforced_concrete: { stroke: '#f87171', glow: 'rgba(248,113,113,0.4)', widthMul: 1.25 },
  glass: { stroke: '#67e8f9', glow: 'rgba(103,232,249,0.42)', widthMul: 1 },
  metal: { stroke: '#fbbf24', glow: 'rgba(251,191,36,0.45)', widthMul: 1.2 },
  wood: { stroke: '#fcd34d', glow: 'rgba(252,211,77,0.35)', widthMul: 1.05 },
  custom: { stroke: '#e879f9', glow: 'rgba(232,121,249,0.35)', widthMul: 1.1 },
}

export function wallDrawStyle(material: WallMaterial): WallDrawStyle {
  return WALL_DRAW_STYLES[material] ?? WALL_DRAW_STYLES.drywall
}

function segmentsIntersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
): boolean {
  const d = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)
  if (Math.abs(d) < 1e-9) return false
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / d
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / d
  return t >= 0 && t <= 1 && u >= 0 && u <= 1
}

/**
 * Wall attenuation when the 2D ray crosses the segment.
 * Low partitions (e.g. 1.2 m glass divider) may not block a ceiling AP at 3 m.
 */
export function wallVerticalAttenuationDb(
  ax: number,
  ay: number,
  apHeightM: number,
  px: number,
  py: number,
  clientHeightM: number,
  wall: FloorWall,
): number {
  if (!segmentsIntersect(ax, ay, px, py, wall.x1, wall.y1, wall.x2, wall.y2)) return 0

  const mx = (wall.x1 + wall.x2) / 2
  const my = (wall.y1 + wall.y2) / 2
  const samples = [
    pathFractionToPoint(ax, ay, px, py, mx, my),
    pathFractionToPoint(ax, ay, px, py, wall.x1, wall.y1),
    pathFractionToPoint(ax, ay, px, py, wall.x2, wall.y2),
  ]
  let minRayH = Infinity
  for (const t of samples) {
    minRayH = Math.min(minRayH, rayHeightAt(apHeightM, clientHeightM, t))
  }

  const { heightM } = resolveWallDimensions(wall)
  const base = materialAttenuation(wall.material, wall.attenuationDb)
  return verticalBarrierAttenuationDb(minRayH, heightM, base)
}

export function createWallSegment(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  material: WallMaterial,
  dims: WallDims,
  attenuationDb?: number,
): FloorWall {
  return {
    id: '',
    x1,
    y1,
    x2,
    y2,
    material,
    heightM: dims.heightM,
    thicknessM: dims.thicknessM,
    attenuationDb,
  }
}
